// @flow
import i18n from "i18next";
import k from "src/i18n/keys";
import React, { Component } from "react";
import ReactDom from "react-dom";
import debounce from "debounce";
import {
  PDFViewer,
  PDFLinkService,
  PDFFindController,
  EventBus
} from "pdfjs-dist/web/pdf_viewer";

import Toolbar from "./Toolbar";

import getBoundingRect from "../lib/get-bounding-rect";
import getClientRects from "../lib/get-client-rects";
import getAreaAsPng from "../lib/get-area-as-png";

import {
  getPageFromRange,
  getPageFromElement,
  findOrCreateContainerLayer
} from "../lib/pdfjs-dom";

import TipContainer from "./TipContainer";
import MouseSelection from "./MouseSelection";

import { scaledToViewport, viewportToScaled } from "../lib/coordinates";

import { Viewer as StyledPDFViewer } from "../styles";
import "pdfjs-dist/web/pdf_viewer.css";
import "../style/pdf_viewer.css";

import "../style/PdfHighlighter.css";

import type {
  T_Position,
  T_ScaledPosition,
  T_Highlight,
  T_Scaled,
  T_LTWH,
  T_PDFJS_Viewer,
  T_PDFJS_Document,
  T_PDFJS_LinkService
} from "../types";

type T_ViewportHighlight<T_HT> = { position: T_Position } & T_HT;

type State<T_HT> = {
  ghostHighlight: ?{
    position: T_ScaledPosition
  },
  isCollapsed: boolean,
  range: ?Range,
  tip: ?{
    highlight: T_ViewportHighlight<T_HT>,
    callback: (highlight: T_ViewportHighlight<T_HT>) => React$Element<*>
  },
  isAreaSelectionInProgress: boolean,
  scrolledToHighlightId: string
};

type Props<T_HT> = {
  highlightTransform: (
    highlight: T_ViewportHighlight<T_HT>,
    index: number,
    setTip: (
      highlight: T_ViewportHighlight<T_HT>,
      callback: (highlight: T_ViewportHighlight<T_HT>) => React$Element<*>
    ) => void,
    hideTip: () => void,
    viewportToScaled: (rect: T_LTWH) => T_Scaled,
    screenshot: (position: T_LTWH) => string,
    isScrolledTo: boolean
  ) => React$Element<*>,
  highlights: Array<T_HT>,
  onScrollChange: () => void,
  scrollRef: (scrollTo: (highlight: T_Highlight) => void) => void,
  pdfDocument: T_PDFJS_Document,
  onSelectionFinished: (
    position: T_ScaledPosition,
    content: { text?: string, image?: string },
    hideTipAndSelection: () => void,
    transformSelection: () => void
  ) => ?React$Element<*>,
  enableAreaSelection: (event: MouseEvent) => boolean
};

const EMPTY_ID = "empty-id";

const disableEvent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  return false;
};
let clickTimeoutId: TimeoutID;

const DEFAULT_SCALE_DELTA = 1.1;
const DEFAULT_SCALE_VALUE = "auto";
const DEFAULT_SCALE = 1.0;
const MIN_SCALE = 0.1;
const MAX_SCALE = 10.0;

class PdfHighlighter<T_HT: T_Highlight> extends Component<
  Props<T_HT>,
  State<T_HT>
> {
  state: State<T_HT> = {
    ghostHighlight: null,
    isCollapsed: true,
    range: null,
    scrolledToHighlightId: EMPTY_ID,
    scale: "auto",
    pageNumber: 1
  };

  viewer = null;
  viewer: T_PDFJS_Viewer;
  linkService: T_PDFJS_LinkService;

  containerNode = null;
  containerNode: ?HTMLDivElement;

  debouncedAfterSelection: () => void;
  // Viewer start
  searchRef: Object;
  searchRef = React.createRef();

  pageNumberRef: Object;
  pageNumberRef = React.createRef();
  containerRef: Object;
  progressBarRef: Object;
  linkService: any;
  pdfFindController: any;
  // handleSearch: Function;
  // handleZoomIn: Function;
  // handleZoomOut: Function;
  // handlePageNoChange: Function;
  // hanldeRotate: Function;
  // handleScaleChange: Function;
  // handleDownload: Function;
  // handleNextPage: Function;
  // handlePreviousPage: Function;
  // handleSearchKeyDown: Function;
  // handlePageNoKeyDown: Function;

  // // Event handler
  // handlePageChanging: Function;
  // handleWindowResize: Function;
  // handleScaleChanging: Function;

  handleZoomIn = () => {
    let newScale = this.viewer.currentScale;
    newScale = Number((newScale * DEFAULT_SCALE_DELTA).toFixed(2));
    newScale = Math.ceil(newScale * 10) / 10;
    newScale = Math.min(MAX_SCALE, newScale);
    this.viewer.currentScaleValue = newScale;
  };

  handleZoomOut = () => {
    let newScale = this.viewer.currentScale;
    newScale = Number((newScale / DEFAULT_SCALE_DELTA).toFixed(2));
    newScale = Math.floor(newScale * 10) / 10;
    newScale = Math.max(MIN_SCALE, newScale);
    this.viewer.currentScaleValue = newScale;
  };

  handleScaleChange = (event: SyntheticInputEvent<HTMLFormElement>) => {
    this.viewer.currentScaleValue = event.target.value;
  };

  handleScaleChanging = ({ presetValue, scale }: Object) => {
    const newScale = presetValue || scale;
    this.viewer.currentPageNumber = this.state.pageNumber;
    this.setState({ scale: newScale });
  };

  handlePageChanging = ({ pageNumber }: Object) => {
    this.setState({ pageNumber: pageNumber });
    this.pageNumberRef.current.value = pageNumber;
  };

  handlePageNoChange = () => {
    //Handle go to page
    const newPageNo = Number(this.pageNumberRef.current.value);
    const { numPages } = this.viewer.pdfDocument;
    if (newPageNo && newPageNo <= numPages && newPageNo >= 1) {
      this.viewer.currentPageNumber = newPageNo;
    }
  };

  handlePageNoKeyDown = (event: SyntheticKeyboardEvent<>) => {
    if (event.keyCode === 13 || event.key === "Enter") {
      this.handlePageNoChange();
    }
  };

  handlePreviousPage = () => {
    if (this.viewer.currentPageNumber > 1) {
      this.viewer.currentPageNumber = this.viewer.currentPageNumber - 1;
    }
  };

  handleNextPage = () => {
    const { numPages } = this.viewer.pdfDocument;
    if (this.viewer.currentPageNumber < numPages) {
      this.viewer.currentPageNumber = this.viewer.currentPageNumber + 1;
    }
  };

  handleSearch = () => {
    // Handle search
    this.pdfFindController.executeCommand("find", {
      query: this.searchRef.current.value,
      phraseSearch: false,
      caseSensitive: false,
      highlightAll: true
    });
  };

  handleSearchKeyDown = (event: SyntheticKeyboardEvent<>) => {
    if (event.keyCode === 13 || event.key === "Enter") {
      this.handleSearch();
    }
  };

  hanldeRotate = (delta: number) => {
    const { pagesRotation } = this.viewer;
    const rotateAngle = (pagesRotation + 360 + delta) % 360;
    this.viewer.pagesRotation = rotateAngle;
  };

  handleDownload = async () => {
    window.open(this.props.url, "_self");
  };

  handleWindowResize = () => {
    const currentScaleValue = this.viewer.currentScaleValue;
    if (
      currentScaleValue === "auto" ||
      currentScaleValue === "page-fit" ||
      currentScaleValue === "page-width"
    ) {
      // Note: the scale is constant for 'page-actual'.
      this.viewer.currentScaleValue = currentScaleValue;
    }
    // this.viewer.update();
  };
  // Viewer end here

  componentWillReceiveProps(nextProps: Props<T_HT>) {
    if (this.props.highlights !== nextProps.highlights) {
      this.renderHighlights(nextProps);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.scale !== nextState.scale) {
      return true;
    }
    return false;
  }

  componentDidMount() {
    const { pdfDocument } = this.props;

    this.debouncedAfterSelection = debounce(this.afterSelection, 500);
    this.linkService = new PDFLinkService({
      externalLinkTarget: 2
    });

    const eventBus = new EventBus();
    this.viewer = new PDFViewer({
      container: this.containerNode,
      enhanceTextSelection: true,
      linkService: this.linkService,
      eventBus
    });

    this.viewer.setDocument(pdfDocument);
    this.linkService.setDocument(pdfDocument);
    this.linkService.setViewer(this.viewer);
    // debug
    window.PdfViewer = this;

    // document.addEventListener("selectionchange", this.onSelectionChange);
    // document.addEventListener("keydown", this.handleKeyDown);

    this.containerNode &&
      this.containerNode.addEventListener("pagesinit", () => {
        this.onDocumentReady();
      });

    this.containerNode &&
      this.containerNode.addEventListener(
        "textlayerrendered",
        this.onTextLayerRendered
      );

    // Viewer
    this.pageNumberRef.current.value = 1;

    this.pdfFindController = new PDFFindController({
      pdfViewer: this.viewer,
      eventBus
    });

    if (this.viewer.setFindController) {
      this.viewer.setFindController(this.pdfFindController);
    }
    // Event binding
    window.addEventListener("resize", this.handleWindowResize);

    this.viewer.eventBus.on("pagechanging", this.handlePageChanging);
    this.viewer.eventBus.on("scalechanging", this.handleScaleChanging);
  }

  componentWillUnmount() {
    document.removeEventListener("selectionchange", this.onSelectionChange);
    document.removeEventListener("keydown", this.handleKeyDown);

    this.containerNode &&
      this.containerNode.removeEventListener(
        "textlayerrendered",
        this.onTextLayerRendered
      );

    // Viewer
    window.removeEventListener("resize", this.handleWindowResize);
    // this.viewer.eventBus.off('updateviewarea', );
    this.viewer.eventBus.off("pagechanging", this.handlePageChanging);
    this.viewer.eventBus.off("scalechanging", this.handleScaleChanging);
    this.viewer.cleanup();
  }

  findOrCreateHighlightLayer(page: number) {
    const textLayer = this.viewer.getPageView(page - 1).textLayer;

    if (!textLayer) {
      return null;
    }

    return findOrCreateContainerLayer(
      textLayer.textLayerDiv,
      "PdfHighlighter__highlight-layer"
    );
  }

  groupHighlightsByPage(highlights: Array<T_HT>): {
    [pageNumber: string]: Array<T_HT>
  } {
    const { ghostHighlight } = this.state;

    return [...highlights, ghostHighlight]
      .filter(Boolean)
      .reduce((res, highlight) => {
        const { pageNumber } = highlight.position;

        res[pageNumber] = res[pageNumber] || [];
        res[pageNumber].push(highlight);

        return res;
      }, {});
  }

  showTip(highlight: T_ViewportHighlight<T_HT>, content: React$Element<*>) {
    const { isCollapsed, ghostHighlight, isAreaSelectionInProgress } =
      this.state;

    const highlightInProgress = !isCollapsed || ghostHighlight;

    if (highlightInProgress || isAreaSelectionInProgress) {
      return;
    }

    this.renderTipAtPosition(highlight.position, content);
  }

  scaledPositionToViewport({
    pageNumber,
    boundingRect,
    rects,
    usePdfCoordinates
  }: T_ScaledPosition): T_Position {
    const viewport = this.viewer.getPageView(pageNumber - 1).viewport;

    return {
      boundingRect: scaledToViewport(boundingRect, viewport, usePdfCoordinates),
      rects: (rects || []).map(rect =>
        scaledToViewport(rect, viewport, usePdfCoordinates)
      ),
      pageNumber
    };
  }

  viewportPositionToScaled({
    pageNumber,
    boundingRect,
    rects
  }: T_Position): T_ScaledPosition {
    const viewport = this.viewer.getPageView(pageNumber - 1).viewport;

    return {
      boundingRect: viewportToScaled(boundingRect, viewport),
      rects: (rects || []).map(rect => viewportToScaled(rect, viewport)),
      pageNumber
    };
  }

  screenshot(position: T_LTWH, pageNumber: number) {
    const canvas = this.viewer.getPageView(pageNumber - 1).canvas;

    return getAreaAsPng(canvas, position);
  }

  renderHighlights(nextProps?: Props<T_HT>) {
    const { highlightTransform, highlights } = nextProps || this.props;

    const { pdfDocument } = this.props;

    const { tip, scrolledToHighlightId } = this.state;

    const highlightsByPage = this.groupHighlightsByPage(highlights);

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
      const highlightLayer = this.findOrCreateHighlightLayer(pageNumber);

      if (highlightLayer) {
        ReactDom.render(
          <div>
            {(highlightsByPage[String(pageNumber)] || []).map(
              (highlight, index) => {
                const { position, ...rest } = highlight;

                const viewportHighlight = {
                  position: this.scaledPositionToViewport(position),
                  ...rest
                };

                if (tip && tip.highlight.id === String(highlight.id)) {
                  this.showTip(tip.highlight, tip.callback(viewportHighlight));
                }

                const isScrolledTo = Boolean(
                  scrolledToHighlightId === highlight.id
                );

                return highlightTransform(
                  viewportHighlight,
                  index,
                  (highlight, callback) => {
                    this.setState({
                      tip: { highlight, callback }
                    });

                    this.showTip(highlight, callback(highlight));
                  },
                  this.hideTipAndSelection,
                  rect => {
                    const viewport = this.viewer.getPageView(
                      pageNumber - 1
                    ).viewport;

                    return viewportToScaled(rect, viewport);
                  },
                  boundingRect => this.screenshot(boundingRect, pageNumber),
                  isScrolledTo
                );
              }
            )}
          </div>,
          highlightLayer
        );
      }
    }
  }

  hideTipAndSelection = () => {
    const tipNode = findOrCreateContainerLayer(
      this.viewer.viewer,
      "PdfHighlighter__tip-layer"
    );

    ReactDom.unmountComponentAtNode(tipNode);

    this.setState({ ghostHighlight: null, tip: null }, () =>
      this.renderHighlights()
    );
  };

  renderTipAtPosition(position: T_Position, inner: ?React$Element<*>) {
    const { boundingRect, pageNumber } = position;

    const page = {
      node: this.viewer.getPageView(pageNumber - 1).div
    };

    const pageBoundingRect = page.node.getBoundingClientRect();

    const tipNode = findOrCreateContainerLayer(
      this.viewer.viewer,
      "PdfHighlighter__tip-layer"
    );

    ReactDom.render(
      <TipContainer
        scrollTop={this.viewer.container.scrollTop}
        pageBoundingRect={pageBoundingRect}
        style={{
          left:
            page.node.offsetLeft + boundingRect.left + boundingRect.width / 2,
          top: boundingRect.top + page.node.offsetTop,
          bottom: boundingRect.top + page.node.offsetTop + boundingRect.height
        }}
        children={inner}
      />,
      tipNode
    );
  }

  onTextLayerRendered = () => {
    this.renderHighlights();
  };

  scrollTo = (highlight: T_Highlight) => {
    const { pageNumber, boundingRect, usePdfCoordinates } = highlight.position;

    this.viewer.container.removeEventListener("scroll", this.onScroll);

    const pageViewport = this.viewer.getPageView(pageNumber - 1).viewport;

    const scrollMargin = 10;

    this.viewer.scrollPageIntoView({
      pageNumber,
      destArray: [
        null,
        { name: "XYZ" },
        ...pageViewport.convertToPdfPoint(
          0,
          scaledToViewport(boundingRect, pageViewport, usePdfCoordinates).top -
            scrollMargin
        ),
        0
      ]
    });

    this.setState(
      {
        scrolledToHighlightId: highlight.id
      },
      () => this.renderHighlights()
    );

    // wait for scrolling to finish
    setTimeout(() => {
      this.viewer.container.addEventListener("scroll", this.onScroll);
    }, 100);
  };

  onDocumentReady = () => {
    const { scrollRef } = this.props;

    this.viewer.currentScaleValue = "auto";

    scrollRef(this.scrollTo);
  };

  onSelectionChange = () => {
    const selection: Selection = window.getSelection();

    if (selection.isCollapsed) {
      this.setState({ isCollapsed: true });
      return;
    }

    const range = selection.getRangeAt(0);

    if (!range) {
      return;
    }

    this.setState({
      isCollapsed: false,
      range
    });

    this.debouncedAfterSelection();
  };

  onScroll = () => {
    const { onScrollChange } = this.props;

    onScrollChange();

    this.setState(
      {
        scrolledToHighlightId: EMPTY_ID
      },
      () => this.renderHighlights()
    );

    this.viewer.container.removeEventListener("scroll", this.onScroll);
  };

  onMouseDown = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    if (event.target.closest(".PdfHighlighter__tip-container")) {
      return;
    }

    this.hideTipAndSelection();
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === "Escape") {
      this.hideTipAndSelection();
    }
  };

  afterSelection = () => {
    const { onSelectionFinished } = this.props;

    const { isCollapsed, range } = this.state;

    if (!range || isCollapsed) {
      return;
    }

    const page = getPageFromRange(range);

    if (!page) {
      return;
    }

    const rects = getClientRects(range, page.node);

    if (rects.length === 0) {
      return;
    }

    const boundingRect = getBoundingRect(rects);

    const viewportPosition = { boundingRect, rects, pageNumber: page.number };

    const content = {
      text: range.toString()
    };
    const scaledPosition = this.viewportPositionToScaled(viewportPosition);

    this.renderTipAtPosition(
      viewportPosition,
      onSelectionFinished(
        scaledPosition,
        content,
        () => this.hideTipAndSelection(),
        () =>
          this.setState(
            {
              ghostHighlight: { position: scaledPosition }
            },
            () => this.renderHighlights()
          )
      )
    );
  };

  toggleTextSelection(flag: boolean) {
    this.viewer.viewer.classList.toggle(
      "PdfHighlighter--disable-selection",
      flag
    );
  }

  render() {
    const { onSelectionFinished, enableAreaSelection } = this.props;
    const { scale, pageNumber } = this.state;
    return (
      <StyledPDFViewer>
        <Toolbar
          searchRef={this.searchRef}
          pageNumberRef={this.pageNumberRef}
          scale={scale}
          numPages={this.props.pdfDocument.numPages}
          pageNumber={pageNumber}
          hanldeRotate={this.hanldeRotate}
          handleSearch={this.handleSearch}
          handleZoomIn={this.handleZoomIn}
          handleZoomOut={this.handleZoomOut}
          handleDownload={this.handleDownload}
          handleNextPage={this.handleNextPage}
          handleScaleChange={this.handleScaleChange}
          handlePreviousPage={this.handlePreviousPage}
          handlePageNoChange={this.handlePageNoChange}
          handlePageNoKeyDown={this.handlePageNoKeyDown}
          handleSearchKeyDown={this.handleSearchKeyDown}
        />
        <div onPointerDown={this.onMouseDown}>
          <div
            ref={node => (this.containerNode = node)}
            className="PdfHighlighter"
            onContextMenu={e => e.preventDefault()}
          >
            <div className="pdfViewer" />
            {typeof enableAreaSelection === "function" ? (
              <MouseSelection
                onDragStart={() => this.toggleTextSelection(true)}
                onDragEnd={() => this.toggleTextSelection(false)}
                onChange={isVisible =>
                  this.setState({ isAreaSelectionInProgress: isVisible })
                }
                shouldStart={event =>
                  enableAreaSelection(event) &&
                  event.target instanceof HTMLElement &&
                  Boolean(event.target.closest(".page"))
                }
                onSelection={(startTarget, boundingRect, resetSelection) => {
                  const page = getPageFromElement(startTarget);

                  if (!page) {
                    return;
                  }

                  const pageBoundingRect = {
                    ...boundingRect,
                    top: boundingRect.top - page.node.offsetTop,
                    left: boundingRect.left - page.node.offsetLeft
                  };

                  const viewportPosition = {
                    boundingRect: pageBoundingRect,
                    rects: [],
                    pageNumber: page.number
                  };

                  const scaledPosition =
                    this.viewportPositionToScaled(viewportPosition);

                  const image = this.screenshot(pageBoundingRect, page.number);

                  this.renderTipAtPosition(
                    viewportPosition,
                    onSelectionFinished(
                      scaledPosition,
                      { image },
                      () => this.hideTipAndSelection(),
                      () =>
                        this.setState(
                          {
                            ghostHighlight: {
                              position: scaledPosition,
                              content: { image }
                            }
                          },
                          () => {
                            resetSelection();
                            this.renderHighlights();
                          }
                        )
                    )
                  );
                }}
              />
            ) : null}
          </div>
        </div>
      </StyledPDFViewer>
    );
  }
}

export default PdfHighlighter;
